import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import { setReportDateFilter } from './reportFiltersActions';
import { getInitialReportDateFilter } from '../../utils/reportDateFilter';
import {
  resolveTokenRange,
  normalizeToken,
  customToken,
  buildPeriodContext,
} from '../../utils/reportPeriod';
import { getStoreTimeZone } from '../../utils/dateTimeUtils';

/**
 * Drop-in replacement for `useState(() => getInitialReportDateFilter(fallback, opts))`. Shares
 * ONE date filter across every report screen via Redux instead of each screen resetting to its
 * own default on mount — switching from Snapshot to Sales to Product keeps the same range.
 *
 * The shared Redux value is only ever written by an explicit call to the returned setter (i.e.
 * the user picking a period in the date control) — never automatically on mount. If the current
 * shared value isn't usable on this page, the page just displays its own default locally for
 * this render; it does NOT push that default back into the shared slot. Writing back on mere
 * display would mean simply *visiting* a page with a different default silently overwrites what
 * another report's user actually chose — e.g. landing on Product (excludeToday, defaults to
 * "Yesterday") would clobber a shared "Today" set by Snapshot, even though nobody clicked
 * "Yesterday". Only `setValue` should ever mutate the shared slot.
 *
 * ── GLOBAL-DP-5 (ZAR-1639): a carried period is RETAINED, not filtered ──────────────
 * Validation used to reject any value outside the destination page's own preset list, so a
 * range chosen on EOD ("Last 3 Months") reverted to the default the moment you navigated to
 * Sales. The spec now requires the opposite: "a period carried from another report matches no
 * quick-select option on the destination report → the period is retained and displayed, and no
 * quick-select is shown active". So a value is valid here whenever the shared resolver can
 * resolve it at all. `options` is retained on the signature (call sites still pass it, and it
 * still drives the overlay's quick-select row via TopBar) but no longer gates the value.
 *
 * Three guards remain:
 *   • `excludeToday` pages reject a literal "Today" — those reports have no live-day data path.
 *   • `excludeTokens` pages reject any listed token, for the same reason generalised to periods
 *     other than the live day (`DPFIX-11`). Retention is the right default, but it assumes the
 *     destination can actually FETCH what it displays; a screen that resolves its own ranges
 *     may not understand every token the global resolver does. Customers is the case in point —
 *     `useCustomerInsights.getCustomerDateRange` has no `thisyear` branch, so a carried
 *     "This Year" silently collapsed to a single day behind a control showing the whole year.
 *     Removing the quick-select pill alone does NOT close that: the shared value is retained
 *     regardless of the destination's `options`, which is the whole point of GLOBAL-DP-5.
 *   • A range whose end has drifted past the current day (a session that crossed midnight) is
 *     clamped to end today, unless the page genuinely selects future dates (Reservations).
 */
export function useSharedReportDateFilter(
  fallback: string,
  opts: { excludeToday?: boolean; excludeTokens?: string[]; options?: string[]; allowFutureDates?: boolean } = {}
): [string, (value: string) => void] {
  const dispatch = useAppDispatch();
  const shared = useAppSelector((s) => s.reportFilters.dateFilter);

  const isValidForPage = (value: string) => {
    if (!value) return false;
    if (opts.excludeToday && normalizeToken(value) === 'today') return false;
    // Compared normalised on both sides, so a caller may spell the token however the preset
    // arrays spell it ("This Year") without having to match the stored casing exactly.
    const normalized = normalizeToken(value);
    if (opts.excludeTokens?.some((t) => normalizeToken(t) === normalized)) return false;
    // Any token the global resolver understands is displayable on every report.
    return resolveTokenRange(value) !== null;
  };

  const setValue = useCallback((value: string) => dispatch(setReportDateFilter(value)), [dispatch]);

  let value = isValidForPage(shared) ? shared : getInitialReportDateFilter(fallback, opts);

  // Midnight drift: a range selected yesterday can resolve to an end date that is now in the
  // future only when the clock has rolled over mid-session. Clamp it on the next report load so
  // the control never shows a period the report cannot fetch. Display-only, like the fallback
  // above — the shared slot still changes solely on an explicit pick.
  if (!opts.allowFutureDates) {
    const range = resolveTokenRange(value);
    const { todayISO } = buildPeriodContext(getStoreTimeZone());
    if (range && range.endDate > todayISO) {
      value = customToken(range.startDate > todayISO ? todayISO : range.startDate, todayISO);
    }
  }

  return [value, setValue];
}
